/**
 * Shared layout constants and formatting for the hour-row grids — Day view
 * and Week view render the same fixed-height rows with the same gutter, and
 * must agree on the pixel geometry or their rows won't line up. Presentational,
 * but kept in utils/ (not calendarDayGeometry.ts, which is deliberately
 * pixel-agnostic) because it's still pure and shared by 2+ components.
 */
import { MIN_VISIBLE_MINUTES, type TimeWindow } from './calendarDayGeometry';
import { EVENT_BLOCK_GAP } from './calendarEventLayout';

/** The project's minimum touch target. */
export const MIN_EVENT_BLOCK_HEIGHT = 44;

/**
 * The row height, derived rather than chosen.
 *
 * The shortest block the grid can draw is MIN_VISIBLE_MINUTES tall —
 * calendarDayGeometry floors the SPAN, deliberately, so overlap lanes are
 * computed on what is actually rendered rather than clamped afterwards on the
 * block (which would push it into the next slot unnoticed). That makes the row
 * height the only honest lever on the minimum block size, so it is solved for
 * instead of picked: at the old 60px hour the shortest block came out 28px
 * tall, a real touch target well under the 44px floor.
 *
 * Trimming the day to business hours is what pays for the taller row — nine
 * hours at this height is far shorter than the twenty-four it replaces.
 */
export const HOUR_HEIGHT = Math.ceil(
  ((MIN_EVENT_BLOCK_HEIGHT + EVENT_BLOCK_GAP) * 60) / MIN_VISIBLE_MINUTES,
);

export const HOURS_IN_DAY = 24;
export const GUTTER_WIDTH = 56;

/** The full-day grid height. Prefer getGridHeight(window) for a trimmed grid. */
export const GRID_HEIGHT = HOUR_HEIGHT * HOURS_IN_DAY;

/**
 * The hour numbers to render for a window. A window that starts mid-hour still
 * gets that whole row — dropping it would leave events with nowhere to land.
 */
export function buildHourRows(window: TimeWindow): number[] {
  const firstHour = Math.floor(window.startMinutes / 60);
  const lastHour = Math.max(firstHour, Math.ceil(window.endMinutes / 60) - 1);

  const rows: number[] = [];
  for (let hour = firstHour; hour <= lastHour && hour < HOURS_IN_DAY; hour += 1) {
    rows.push(hour);
  }
  return rows;
}

export function getGridHeight(window: TimeWindow): number {
  return buildHourRows(window).length * HOUR_HEIGHT;
}

/**
 * Where to scroll so the now line sits in the middle of the viewport, clamped
 * so it never overscrolls either end. 0 when the whole grid already fits, or
 * when the viewport has not been measured yet — scrolling to a guess is worse
 * than not scrolling.
 */
export function getScrollToNowOffset(
  nowRatio: number,
  gridHeight: number,
  viewportHeight: number,
): number {
  if (viewportHeight <= 0 || gridHeight <= viewportHeight) return 0;

  const nowY = nowRatio * gridHeight;
  const maxOffset = gridHeight - viewportHeight;

  return Math.round(Math.min(Math.max(nowY - viewportHeight / 2, 0), maxOffset));
}

export function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}
